import React, { Fragment, useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CircleIcon from '@mui/icons-material/FiberManualRecord';
import IconButton from 'components/IconButton';
import padStart from 'lodash/padStart';
import useMediaRecorder from 'hooks/useMediaRecorder';
import VideoSettingsModal from './VideoSettingsModal';
import { SettingsIcon } from 'components/icons';
import Typography from '@mui/material/Typography';

const Root = styled('div')(
  ({ theme }) => `
  border-radius: 6px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  ${({ theme }) => `${theme.breakpoints.down('lg')} {
    flex-direction: column;
  }`}
  .video-container {
    .recorder-video {
      position: relative;
    }
  }
  .video-actions {
    position: absolute;
    width: 100%;
    bottom: 8px;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    button {
      color: red;
      background: white;
      font-size: 28px;
    }
    .timer {
      color: white;
      background-color: rgba(0, 8, 102, 0.5);
      font-size: 14px;
      font-weight: 500;
      border-radius: 100px;
      padding: 5px 12px;
      margin-left: 8px;
      min-width: 65px;
      text-align: center;
      position: absolute;
      right: 10px;
    }
  }
  button.settings {
    background: none;
    border: none;
    color: white;
    font-size: 28px;
    position: absolute;
    top: 10px;
    right: 10px;
  }
  .covers {
    display: flex;
    flex-wrap: wrap;
    max-height: 300px;
    overflow-y: auto;
    img {
      width: 45%;
      height: auto;
      object-fit: cover;
      margin: 0 10px 10px 0;
      cursor: pointer;
    }
    .selected {
      border: 2px solid #04abff;
    }
  }
  .custom-cover {
    width: 45%;
    position: relative;
    overflow: hidden;
    border: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    margin: 0 10px 10px 0;
    cursor: pointer;
    &:hover {
      background-color: ${theme.palette.background.default};;
    }
    label {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0;
      cursor: pointer;
      padding: 10px;
    }
    input[type='file'] {
      font-size: 100px;
      position: absolute;
      left: 0;
      top: 0;
      opacity: 0;
      width: 0;
    }
  }
  video {
    transform: scale(-1, 1);
    object-fit: cover;
  }
`,
);

const secondsToPretty = (sec) => {
  if (sec < 60) return `00:${padStart(sec, 2, 0)}`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${padStart(m, 2, 0)}:${padStart(s, 2, 0)}`;
};

// TODO
const SafariMessage = () => (
  <Box>
    <Typography variant="subtitle1">
      How To Enable Webcam Recording in Latest Safari:
    </Typography>
    <Typography fontWeight={600}>For Desktop</Typography>
    <ol>
      <li>Show Develop menu from Safari > Preferences > Advanced.</li>
      <li>
        Toggle Develop > Experimental Features > MediaRecorder menu to enable
        video recording.
      </li>
      <li>Reload page to activate feature.</li>
    </ol>
    <Typography fontWeight={600}>For iOS:</Typography>
    <ol>
      <li>Go to Settings → Safari → Advanced → Experimental Features</li>
      <li>Enable MediaRecorder</li>
      <li>Reload page to activate feature.</li>
    </ol>
  </Box>
);

const VideoRecorder = ({ onChange, attachment, values }) => {
  const playerRef = useRef(null);
  const timerRef = useRef();
  const [showSettings, setShowSettings] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showRecording, setShowRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const {
    setCaptureRef,
    devices,
    data,
    stream,
    err,
    clearRecorder,
    setDevices,
    isSupported,
  } = useMediaRecorder({
    isRecording,
    audioOnly: false,
  });

  useEffect(() => {
    if (!values.video) {
      setRecordingUrl();
      clearRecorder();
      setShowRecording(false);
    }
  }, [values.video]);

  useEffect(() => {
    if (err) {
      setShowPreview(false);
    }
  }, [err]);

  useEffect(() => {
    if (data && showRecording) {
      const options = {
        type: 'video/webm',
      };
      const blob = new Blob(data, options);
      const url = URL.createObjectURL(blob);
      setRecordingUrl(url);
      onChange('video', blob);
    }
  }, [data]);

  const onStartRecording = (e) => {
    e.preventDefault();
    setIsRecording(true);
    setShowRecording(false);
    setRecordingUrl(null);
    setTimer(0);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        return t + 1;
      });
    }, 1000);
  };

  const onStopRecording = (e) => {
    e.preventDefault();
    setIsRecording(false);
    clearInterval(timerRef.current);
    setShowRecording(true);
  };

  const updateMediaSettings = (settings) => {
    setDevices(settings);
    setShowSettings(false);
  };

  const onPlay = () => {
    playerRef.current.play();
    setIsPlaying(true);
  };

  const onStopPlayer = () => {
    playerRef.current.pause();
    setIsPlaying(false);
  };

  const onVideoEnded = () => {
    setIsPlaying(false);
  };

  return (
    <Root>
      <div className="video-container">
        {err && <Alert variant="danger">{err}</Alert>}
        <div className="recorder-video">
          <div className="video-inner">
            {showPreview && !showRecording && (
              <div className="embed-responsive embed-responsive-16by9">
                <video
                  autoPlay
                  id="capture-video"
                  muted="muted"
                  ref={setCaptureRef}
                  className="capture-video"
                />
              </div>
            )}
            {showRecording && (
              <div className="embed-responsive embed-responsive-16by9">
                <video
                  ref={playerRef}
                  onEnded={onVideoEnded}
                  className="recording-video1"
                  src={recordingUrl}
                />
              </div>
            )}
          </div>
          {isSupported === false && (
            <Box
              zIndex={1}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgcolor="background.paper"
              p={2}
            >
              <Typography variant="h6" color="textPrimary">
                Your browser does not support video recording.
              </Typography>
              <Typography variant="body1" color="textPrimary">
                Please use latest version of Firefox, Edge or Chrome.
              </Typography>
            </Box>
          )}
          {stream.id && (
            <div className="video-actions">
              {!isRecording && !showRecording && !recordingUrl && (
                <IconButton
                  size="large"
                  icon={<CircleIcon fontSize="inherit" />}
                  onClick={onStartRecording}
                  className="record"
                  disabled={!stream.id}
                />
              )}
              {isRecording && (
                <IconButton
                  size="large"
                  icon={<StopIcon fontSize="inherit" />}
                  onClick={onStopRecording}
                  className="stop"
                />
              )}
              {recordingUrl && !isRecording && (
                <Fragment>
                  {!isPlaying && (
                    <IconButton
                      size="large"
                      icon={<PlayIcon fontSize="inherit" />}
                      onClick={onPlay}
                      className="play"
                    />
                  )}
                  {isPlaying && (
                    <IconButton
                      size="large"
                      icon={<StopIcon fontSize="inherit" />}
                      onClick={onStopPlayer}
                      className="stop"
                    />
                  )}
                </Fragment>
              )}
              <div className="timer">{secondsToPretty(timer)}</div>
            </div>
          )}
          <IconButton
            icon={<SettingsIcon />}
            onClick={() => setShowSettings(true)}
            className="settings"
            size="large"
          />
        </div>
      </div>
      {showSettings && (
        <VideoSettingsModal
          onSubmit={updateMediaSettings}
          initialSettings={devices}
          onClose={() => setShowSettings(false)}
        />
      )}
    </Root>
  );
};

export default VideoRecorder;
